import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Alert, Typography, CircularProgress, Stack } from '@mui/material';
import { CreditCard as CreditCardIcon, Lock as LockIcon } from '@mui/icons-material';
import logger from '../../utils/logger';

const SDK_URLS = {
  sandbox: 'https://sandbox.web.squarecdn.com/v1/square.js',
  production: 'https://web.squarecdn.com/v1/square.js',
};

// Load Square's Web Payments SDK once per page (sandbox and production can't be mixed)
let sdkPromise = null;
const loadSquareSdk = (environment) => {
  if (window.Square) return Promise.resolve(window.Square);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SDK_URLS[environment] || SDK_URLS.production;
      script.async = true;
      script.onload = () => (window.Square ? resolve(window.Square) : reject(new Error('Square SDK unavailable')));
      script.onerror = () => {
        sdkPromise = null;
        reject(new Error('Could not load the secure card form'));
      };
      document.head.appendChild(script);
    });
  }
  return sdkPromise;
};

const newIdempotencyKey = () =>
  (window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`);

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

/**
 * Pay an invoice from its public link with Square (SQUARE_INTEGRATION_PLAN.md §5.1).
 * The card is tokenized in Square's iframe; the server prices and charges it.
 *
 * Props:
 *   token     – signed document token from the URL
 *   customer  – { name, email } for buyer verification (optional)
 *   onPaid(document) – called with the updated (paid) invoice
 */
const SquareInvoicePayForm = ({ token, customer, onPaid }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardReady, setCardReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const cardRef = useRef(null);
  // One key per payment attempt; reset after a decline so a new card is a new attempt
  const idempotencyKeyRef = useRef(newIdempotencyKey());

  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const containerId = 'square-card-container';

  // 1. What can this invoice be paid with?
  useEffect(() => {
    let cancelled = false;
    fetch(`${apiBase}/api/public/documents/${token}/pay-config`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setConfig(data);
      })
      .catch((err) => {
        logger.log('[SquareInvoicePayForm] pay-config failed:', err.message);
        if (!cancelled) setConfig({ available: false, reason: 'Online payment is temporarily unavailable.' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, token]);

  // 2. Mount Square's card iframe
  useEffect(() => {
    if (!config || !config.available) return undefined;
    let card;
    let cancelled = false;
    (async () => {
      try {
        const Square = await loadSquareSdk(config.environment);
        const payments = Square.payments(config.applicationId, config.locationId);
        card = await payments.card();
        if (cancelled) {
          card.destroy();
          return;
        }
        await card.attach(`#${containerId}`);
        cardRef.current = card;
        setCardReady(true);
      } catch (err) {
        logger.log('[SquareInvoicePayForm] card init failed:', err.message);
        if (!cancelled) setError('The secure card form could not be loaded. Please refresh the page.');
      }
    })();
    return () => {
      cancelled = true;
      cardRef.current = null;
      if (card) card.destroy().catch(() => {});
    };
  }, [config]);

  const handlePay = async () => {
    if (!cardRef.current || processing) return;
    setProcessing(true);
    setError('');
    try {
      const [givenName, ...rest] = (customer?.name || '').trim().split(/\s+/);
      const result = await cardRef.current.tokenize({
        amount: (config.amountCents / 100).toFixed(2),
        currencyCode: config.currency || 'USD',
        intent: 'CHARGE',
        customerInitiated: true,
        sellerKeyedIn: false,
        billingContact: {
          ...(givenName ? { givenName } : {}),
          ...(rest.length ? { familyName: rest.join(' ') } : {}),
          ...(customer?.email ? { email: customer.email } : {}),
        },
      });
      if (result.status !== 'OK') {
        // Field-level problems (missing CVV, bad number…) are shown inside the iframe too
        const message = result.errors && result.errors[0] && result.errors[0].message;
        throw new Error(message || 'Please check your card details.');
      }

      const res = await fetch(`${apiBase}/api/public/documents/${token}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceId: result.token, idempotencyKey: idempotencyKeyRef.current }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // A declined card or a new attempt needs a fresh key; a 500 keeps it so a retry can't double-charge
        if (res.status < 500) idempotencyKeyRef.current = newIdempotencyKey();
        throw new Error(data.error || 'Payment failed. Please try again.');
      }
      onPaid(data.document);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!config || !config.available) {
    return config && config.reason ? <Alert severity="info">{config.reason}</Alert> : null;
  }

  return (
    <Box>
      {config.environment === 'sandbox' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Test mode — use a Square sandbox test card (e.g. 4111 1111 1111 1111). No real money moves.
        </Alert>
      )}
      <Box id={containerId} sx={{ minHeight: 90 }} />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Button
        fullWidth
        size="large"
        variant="contained"
        onClick={handlePay}
        disabled={!cardReady || processing}
        startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <CreditCardIcon />}
      >
        {processing ? 'Processing…' : `Pay ${money(config.amountCents)}`}
      </Button>
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
        <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          Secure payment processed by Square. Your card details never touch our servers.
        </Typography>
      </Stack>
    </Box>
  );
};

export default SquareInvoicePayForm;
